// ============================================================================
// PROMPT 111 — Advanced Playlist Builder, NL Playlist Planning, Platform-Aware Sequencing
// ============================================================================
// Owns: playlist intent model, NL playlist request parsing, planning engine, platform-aware
// sequencing, playlist scoring, readiness report, and playlist->set conversion.
// Does NOT own: Spotify/SoundCloud remote writes, DistroKid submission, AI provider internals,
// rights ledger internals, audio analysis internals (those are 112-120 / canonical foundations).
//
// Follows the established value-add idiom: pure, deterministic, stateless (callers persist via
// existing project/set routes). Fixed timestamp + SeqCounter keep outputs deterministic for tests.
// Reuses the transition risk scorer from the canonical Transition Assistant rather than
// duplicating BPM/key compatibility logic.
import { SeqCounter } from "@djsem/shared";
import { DJSEMTransitionRiskAnalyzer } from "./transitionAssistant";
import type { P0TrackInput } from "./valueAddP0";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ---- contracts (from spec §5) ----

/** The destination a playlist is being built for; drives platform-readiness checks during planning. */
export type DJSEMPlaylistTarget =
  | "djsem_internal"
  | "spotify"
  | "soundcloud"
  | "rekordbox_export"
  | "release_campaign"
  | "event_client"
  | "public_page"
  | "distrokid_support";

/** The energy shape a playlist should follow across its length. */
export type DJSEMPlaylistEnergyArc = "warmup" | "build" | "peak" | "closing" | "steady" | "custom";
/** How explicit-content tracks should be handled when building a playlist. */
export type DJSEMPlaylistExplicitPolicy = "allow" | "avoid" | "block";

/** A structured, editable request describing the constraints for a playlist to be built. */
export interface DJSEMPlaylistBuildRequest {
  id: string;
  organisationId?: string;
  projectId?: string;
  prompt?: string;
  target: DJSEMPlaylistTarget;
  title?: string;
  desiredDurationMinutes?: number;
  bpmMin?: number;
  bpmMax?: number;
  energyArc?: DJSEMPlaylistEnergyArc;
  allowedGenres: string[];
  excludedGenres: string[];
  allowedTags: string[];
  excludedTags: string[];
  explicitPolicy: DJSEMPlaylistExplicitPolicy;
  sourceCollectionIds: string[];
  requiredTrackIds: string[];
  excludedTrackIds: string[];
  platformProvider?: "spotify" | "soundcloud" | "none";
  createdBy?: string;
  createdAt: string;
}

/** Machine-readable code identifying the kind of issue found during playlist planning/readiness checks. */
export type DJSEMPlaylistWarningCode =
  | "missing_platform_ref"
  | "missing_local_file"
  | "rights_unknown"
  | "explicit_content"
  | "duplicate_track"
  | "weak_transition"
  | "duration_short"
  | "duration_long"
  | "metadata_low_confidence";

/** A single warning attached to a playlist plan or one of its items. */
export interface DJSEMPlaylistPlanWarning {
  code: DJSEMPlaylistWarningCode;
  severity: "info" | "warning" | "error";
  message: string;
  itemIds: string[];
}

/** A single track slot within a generated playlist plan, with its order, provenance, and readiness flags. */
export interface DJSEMPlaylistPlanItem {
  id: string;
  trackId?: string;
  platformTrackRefId?: string;
  title: string;
  artist?: string;
  order: number;
  reason: string;
  bpm?: number;
  key?: string;
  energy?: number;
  explicit?: boolean;
  platformReady: boolean;
  rightsReady: boolean;
  warnings: string[];
}

/** A fully sequenced, scored playlist produced from a build request and a candidate track pool. */
export interface DJSEMPlaylistPlan {
  id: string;
  requestId: string;
  title: string;
  target: DJSEMPlaylistTarget;
  items: DJSEMPlaylistPlanItem[];
  durationMs: number;
  readinessScore: number;
  energyCurve: Array<{ index: number; energy: number; trackId?: string }>;
  warnings: DJSEMPlaylistPlanWarning[];
  createdAt: string;
}

/** Export/publish readiness verdict for a playlist plan, splitting warnings into blocking vs advisory. */
export interface DJSEMPlaylistReadinessReport {
  planId: string;
  readinessScore: number;
  readyForExport: boolean;
  readyForPublish: boolean;
  blocking: DJSEMPlaylistPlanWarning[];
  advisory: DJSEMPlaylistPlanWarning[];
  summary: string;
  createdAt: string;
}

/**
 * Playlist track carries the P0 superset plus platform/rights/explicit provenance the planner needs.
 */
export interface PlaylistTrackInput extends P0TrackInput {
  explicit?: boolean;
  durationMs?: number;
  platformTrackRefId?: string;       // present => has a platform ref (e.g. spotify uri)
  hasLocalFile?: boolean;            // present + true => local audio available
  rightsStatus?: "cleared" | "unknown" | "restricted";
  metadataConfidence?: number;       // 0..1
}

/**
 * Set shape returned by `convertToSet` (structural; reuses canonical set idea, not a store).
 */
export interface DJSEMSetDraft {
  id: string;
  fromPlaylistId: string;
  title: string;
  sections: Array<{ label: string; intent: string; itemIds: string[] }>;
  items: Array<{ id: string; trackId?: string; order: number; title: string }>;
  createdAt: string;
}

// ============================================================================
// Story 111.1 — Natural language playlist request parser
// ============================================================================

/** Input to the natural-language playlist request parser. */
export interface ParsePlaylistIntentInput {
  prompt: string;
  target?: DJSEMPlaylistTarget;
  sourceCollectionIds?: string[];
  requiredTrackIds?: string[];
  organisationId?: string;
  projectId?: string;
}

/** Parses plain-English playlist requests into structured {@link DJSEMPlaylistBuildRequest} objects. */
export class DJSEMPlaylistIntentParser {
  /**
   * Parses a plain-English playlist prompt into a structured, editable build request. Pure.
   * Extracts duration, BPM range, energy arc, target platform, explicit policy, and genre/tag hints
   * via regex/keyword matching over the prompt text.
   * @param input - the free-text prompt plus optional overrides (target, source collections, required tracks).
   * @returns a fully-populated, editable {@link DJSEMPlaylistBuildRequest}.
   */
  parse(input: ParsePlaylistIntentInput): DJSEMPlaylistBuildRequest {
    const q = (input.prompt ?? "").toLowerCase();

    // duration: "90-minute", "90 min", "45 minutes", "1 hour", "1.5 hour"
    let desiredDurationMinutes: number | undefined;
    const hoursDec = q.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\b/);
    const mins = q.match(/(\d{2,3})\s*(?:-?\s*)?(?:minutes?|mins?|min)\b/);
    if (mins) desiredDurationMinutes = +mins[1];
    else if (hoursDec) desiredDurationMinutes = Math.round(parseFloat(hoursDec[1]) * 60);

    // bpm range: "118-123 bpm", "between 120 and 130 bpm", "under 125 bpm"
    let bpmMin: number | undefined, bpmMax: number | undefined;
    const bpmRange = q.match(/(\d{2,3})\s*[-–to]+\s*(\d{2,3})\s*bpm/) || q.match(/between\s+(\d{2,3})\s+and\s+(\d{2,3})\s*bpm/);
    const bpmUnder = q.match(/(?:under|below|<)\s*(\d{2,3})\s*bpm/);
    const bpmOver = q.match(/(?:over|above|>)\s*(\d{2,3})\s*bpm/);
    if (bpmRange) { bpmMin = Math.min(+bpmRange[1], +bpmRange[2]); bpmMax = Math.max(+bpmRange[1], +bpmRange[2]); }
    else if (bpmUnder) bpmMax = +bpmUnder[1];
    else if (bpmOver) bpmMin = +bpmOver[1];

    // energy arc
    let energyArc: DJSEMPlaylistEnergyArc = "steady";
    if (/\b(warm[- ]?up|warmup|opening|opener)\b/.test(q)) energyArc = "warmup";
    else if (/\b(peak|peak[- ]?time|banger|main)\b/.test(q)) energyArc = "peak";
    else if (/\b(clos(?:e|ing)|cool[- ]?down|last)\b/.test(q)) energyArc = "closing";
    else if (/\b(build|rise|ascend)\b/.test(q)) energyArc = "build";

    // target platform / use case
    let target: DJSEMPlaylistTarget = input.target ?? "djsem_internal";
    let platformProvider: "spotify" | "soundcloud" | "none" = "none";
    if (/\bspotify\b/.test(q)) { target = input.target ?? "spotify"; platformProvider = "spotify"; }
    else if (/\bsoundcloud\b/.test(q)) { target = input.target ?? "soundcloud"; platformProvider = "soundcloud"; }
    else if (/\brekordbox\b/.test(q)) target = input.target ?? "rekordbox_export";
    else if (/\b(release|campaign|single|ep|album)\b/.test(q)) target = input.target ?? "release_campaign";
    else if (/\bdistrokid\b/.test(q)) target = input.target ?? "distrokid_support";
    else if (/\b(event|client|wedding|corporate)\b/.test(q)) target = input.target ?? "event_client";
    else if (/\b(fan|public)\b/.test(q)) target = input.target ?? "public_page";

    // explicit policy
    let explicitPolicy: DJSEMPlaylistExplicitPolicy = "allow";
    if (/\bno explicit|clean only|radio[- ]?edit|family[- ]?friendly\b/.test(q)) explicitPolicy = "block";
    else if (/\bavoid explicit|prefer clean\b/.test(q)) explicitPolicy = "avoid";

    // genres / tags (allow-list from common words present in the prompt)
    const genreWords = ["melodic techno", "techno", "house", "trance", "dnb", "drum & bass", "disco", "ambient", "hip hop", "afro", "melodic", "progressive"];
    const allowedGenres = genreWords.filter((g) => q.includes(g));
    const tagWords = ["beach", "sunset", "rooftop", "underground", "vocal", "instrumental", "hidden gems", "new"];
    const allowedTags = tagWords.filter((t) => q.includes(t));

    return {
      id: `plreq_${_seq.next()}`,
      organisationId: input.organisationId,
      projectId: input.projectId,
      prompt: input.prompt,
      target,
      title: undefined,
      desiredDurationMinutes,
      bpmMin,
      bpmMax,
      energyArc,
      allowedGenres,
      excludedGenres: [],
      allowedTags,
      excludedTags: [],
      explicitPolicy,
      sourceCollectionIds: input.sourceCollectionIds ?? [],
      requiredTrackIds: input.requiredTrackIds ?? [],
      excludedTrackIds: [],
      platformProvider,
      createdBy: undefined,
      createdAt: AT,
    };
  }
}

// ============================================================================
// Story 111.2/111.3/111.4 — Platform-aware planning, sequencing, scoring
// ============================================================================

// Target energy shape per arc, sampled 0..1 across the playlist length.
function arcEnergyAt(arc: DJSEMPlaylistEnergyArc | undefined, pos: number): number {
  // pos in 0..1
  switch (arc) {
    case "warmup": return 0.3 + pos * 0.25;                       // gentle rise, stays low-mid
    case "build": return 0.35 + pos * 0.5;                        // steady climb
    case "peak": return 0.7 + Math.sin(pos * Math.PI) * 0.15;     // high, arched
    case "closing": return 0.7 - pos * 0.4;                       // descend
    case "steady": return 0.55;
    default: return 0.5 + Math.sin(pos * Math.PI) * 0.2;          // custom: gentle arc
  }
}

/** Builds ordered, platform-aware playlist plans from a build request and candidate track pool. */
export class DJSEMPlaylistPlanningService {
  private transition = new DJSEMTransitionRiskAnalyzer();

  /**
   * Builds an ordered, platform-aware plan from a request + a candidate track pool. Pure.
   * Filters the pool by request constraints, sequences tracks to fit the target energy arc,
   * attaches per-item and transition-risk warnings, and scores overall readiness.
   * @param request - the playlist build request describing constraints and target platform.
   * @param tracks - the candidate track pool to select and sequence from.
   * @returns the resulting playlist plan with items, warnings, and a readiness score.
   */
  createPlan(request: DJSEMPlaylistBuildRequest, tracks: PlaylistTrackInput[]): DJSEMPlaylistPlan {
    // 1) filter by request constraints (bpm, genre, tag, explicit policy, exclusions)
    const excluded = new Set(request.excludedTrackIds);
    let pool = tracks.filter((t) => !excluded.has(t.id));
    if (request.bpmMin != null) pool = pool.filter((t) => t.bpm == null || t.bpm >= request.bpmMin! - 2);
    if (request.bpmMax != null) pool = pool.filter((t) => t.bpm == null || t.bpm <= request.bpmMax! + 2);
    if (request.allowedGenres.length) pool = pool.filter((t) => !t.genre || request.allowedGenres.some((g) => (t.genre ?? "").toLowerCase().includes(g)));
    if (request.explicitPolicy === "block") pool = pool.filter((t) => !t.explicit);

    // 2) required tracks always included, pinned into the ordering by best-fit energy slot later
    const required = tracks.filter((t) => request.requiredTrackIds.includes(t.id));
    const requiredIds = new Set(required.map((t) => t.id));
    const optional = pool.filter((t) => !requiredIds.has(t.id));

    // 3) target count from desired duration (assume ~3.5min avg when durations unknown)
    const avgMs = 3.5 * 60_000;
    const targetCount = request.desiredDurationMinutes
      ? Math.max(1, Math.round((request.desiredDurationMinutes * 60_000) / avgMs))
      : Math.min(optional.length + required.length, 20);

    // 4) order optional pool along the arc: sort by energy, then place along the arc curve
    const chosen = [...required, ...optional].slice(0, Math.max(targetCount, required.length));
    const withEnergy = chosen.map((t) => ({ t, e: t.energy ?? 0.5 }));
    // greedy: assign each slot the remaining track whose energy best matches the arc target
    const slots = withEnergy.length;
    const remaining = [...withEnergy];
    const ordered: PlaylistTrackInput[] = [];
    for (let i = 0; i < slots; i++) {
      const targetE = arcEnergyAt(request.energyArc, slots <= 1 ? 0 : i / (slots - 1));
      let bestIdx = 0, bestDiff = Infinity;
      for (let j = 0; j < remaining.length; j++) {
        const diff = Math.abs(remaining[j].e - targetE);
        if (diff < bestDiff) { bestDiff = diff; bestIdx = j; }
      }
      ordered.push(remaining.splice(bestIdx, 1)[0].t);
    }

    // 5) build items + per-item warnings (platform ref, local file, rights, explicit, metadata)
    const seen = new Set<string>();
    const items: DJSEMPlaylistPlanItem[] = ordered.map((t, idx) => {
      const w: string[] = [];
      const platformReady = request.platformProvider && request.platformProvider !== "none"
        ? !!t.platformTrackRefId : true;
      if (request.platformProvider && request.platformProvider !== "none" && !t.platformTrackRefId) w.push("missing_platform_ref");
      if (t.hasLocalFile === false && (request.target === "rekordbox_export" || request.target === "djsem_internal")) w.push("missing_local_file");
      const rightsReady = t.rightsStatus !== "restricted";
      if (t.rightsStatus == null || t.rightsStatus === "unknown") w.push("rights_unknown");
      if (t.explicit && request.explicitPolicy !== "allow") w.push("explicit_content");
      if ((t.metadataConfidence ?? 1) < 0.5) w.push("metadata_low_confidence");
      if (seen.has(t.id)) w.push("duplicate_track");
      seen.add(t.id);
      return {
        id: `pli_${_seq.next()}`,
        trackId: t.id,
        platformTrackRefId: t.platformTrackRefId,
        title: t.title ?? t.id,
        artist: t.artist,
        order: idx,
        reason: reasonFor(t, request, idx, slots),
        bpm: t.bpm,
        key: t.key,
        energy: t.energy,
        explicit: t.explicit,
        platformReady,
        rightsReady,
        warnings: w,
      };
    });

    // 6) transition warnings between consecutive items (reuse canonical transition scorer)
    const warnings: DJSEMPlaylistPlanWarning[] = [];
    for (let i = 0; i < items.length - 1; i++) {
      const a = items[i], b = items[i + 1];
      const report = this.transition.analysePair({
        fromTrack: { id: a.trackId ?? a.id, bpm: a.bpm, key: a.key, energy: a.energy },
        toTrack: { id: b.trackId ?? b.id, bpm: b.bpm, key: b.key, energy: b.energy },
      });
      const risk = 1 - report.compatibilityScore;
      if (risk >= 0.4) {
        const reasons = report.issues.filter((iss) => iss.severity === "high" || iss.severity === "warning").map((iss) => iss.explanation);
        warnings.push({
          code: "weak_transition",
          severity: risk >= 0.6 ? "error" : "warning",
          message: `Weak transition ${a.title} → ${b.title}${reasons.length ? ": " + reasons.join("; ") : ""}`,
          itemIds: [a.id, b.id],
        });
      }
    }

    // 7) aggregate item warnings into plan-level warnings
    const codeAgg: Record<string, string[]> = {};
    for (const it of items) for (const c of it.warnings) (codeAgg[c] ??= []).push(it.id);
    for (const [code, ids] of Object.entries(codeAgg)) {
      warnings.push({
        code: code as DJSEMPlaylistWarningCode,
        severity: code === "explicit_content" || code === "rights_unknown" ? "warning" : code === "missing_local_file" ? "warning" : "info",
        message: warningMessage(code as DJSEMPlaylistWarningCode, ids.length),
        itemIds: ids,
      });
    }

    // 8) duration warnings
    const durationMs = ordered.reduce((s, t) => s + (t.durationMs ?? avgMs), 0);
    if (request.desiredDurationMinutes) {
      const targetMs = request.desiredDurationMinutes * 60_000;
      if (durationMs < targetMs * 0.85) warnings.push({ code: "duration_short", severity: "warning", message: `Playlist is shorter than the ${request.desiredDurationMinutes}-min target.`, itemIds: [] });
      if (durationMs > targetMs * 1.15) warnings.push({ code: "duration_long", severity: "info", message: `Playlist exceeds the ${request.desiredDurationMinutes}-min target.`, itemIds: [] });
    }

    const energyCurve = items.map((it, i) => ({ index: i, energy: it.energy ?? 0.5, trackId: it.trackId }));
    const readinessScore = scorePlanItems(items, warnings);

    return {
      id: `plplan_${_seq.next()}`,
      requestId: request.id,
      title: request.title ?? deriveTitle(request),
      target: request.target,
      items,
      durationMs,
      readinessScore,
      energyCurve,
      warnings,
      createdAt: AT,
    };
  }

  /**
   * Generates N alternative sequences (shuffle within energy bands) for A/B review. Pure.
   * @param request - the base playlist build request.
   * @param tracks - the candidate track pool to select and sequence from.
   * @param count - how many alternative plans to generate (default 2).
   * @returns an array of `count` alternative {@link DJSEMPlaylistPlan}s, each using a different energy arc variant.
   */
  generateAlternatives(request: DJSEMPlaylistBuildRequest, tracks: PlaylistTrackInput[], count = 2): DJSEMPlaylistPlan[] {
    const out: DJSEMPlaylistPlan[] = [];
    for (let n = 0; n < count; n++) {
      // rotate energyArc variants to produce genuinely different orderings
      const arcs: DJSEMPlaylistEnergyArc[] = ["steady", "build", "peak", "warmup", "closing"];
      const alt = { ...request, energyArc: arcs[(arcs.indexOf(request.energyArc ?? "steady") + n + 1) % arcs.length] };
      out.push(this.createPlan(alt, tracks));
    }
    return out;
  }

  /**
   * Converts a plan into a set draft, preserving order and generating sections from the arc. Pure.
   * @param plan - the playlist plan to convert.
   * @returns a {@link DJSEMSetDraft} with items grouped into opening/main/closing (or a single) section(s).
   */
  convertToSet(plan: DJSEMPlaylistPlan): DJSEMSetDraft {
    // split into 3 sections by position (intro/main/outro) preserving order
    const n = plan.items.length;
    const sec = (label: string, intent: string, from: number, to: number) => ({
      label, intent, itemIds: plan.items.slice(from, to).map((i) => i.id),
    });
    const sections = n >= 3
      ? [sec("Opening", "warmup", 0, Math.ceil(n / 3)), sec("Main", "peak", Math.ceil(n / 3), Math.ceil((2 * n) / 3)), sec("Closing", "closing", Math.ceil((2 * n) / 3), n)]
      : [sec("Set", "steady", 0, n)];
    return {
      id: `setdraft_${_seq.next()}`,
      fromPlaylistId: plan.id,
      title: plan.title,
      sections,
      items: plan.items.map((i) => ({ id: i.id, trackId: i.trackId, order: i.order, title: i.title })),
      createdAt: AT,
    };
  }
}

// ============================================================================
// Story 111.4 — Readiness service
// ============================================================================

/** Evaluates a playlist plan's export/publish readiness from its warnings and item states. */
export class DJSEMPlaylistReadinessService {
  /**
   * Splits a plan's warnings into blocking vs advisory + computes publish/export readiness. Pure.
   * @param plan - the playlist plan to evaluate.
   * @returns a {@link DJSEMPlaylistReadinessReport} with blocking/advisory warnings and export/publish readiness flags.
   */
  evaluate(plan: DJSEMPlaylistPlan): DJSEMPlaylistReadinessReport {
    const blocking = plan.warnings.filter((w) => w.severity === "error"
      || (w.code === "rights_unknown") || (w.code === "explicit_content" && plan.target !== "djsem_internal"));
    const advisory = plan.warnings.filter((w) => !blocking.includes(w));
    const readyForExport = !blocking.some((w) => w.code === "missing_local_file" || w.code === "weak_transition" && w.severity === "error");
    const readyForPublish = blocking.length === 0
      && !plan.items.some((i) => !i.platformReady && plan.target !== "djsem_internal" && plan.target !== "rekordbox_export");
    return {
      planId: plan.id,
      readinessScore: plan.readinessScore,
      readyForExport,
      readyForPublish,
      blocking,
      advisory,
      summary: `${plan.items.length} tracks · readiness ${(plan.readinessScore * 100) | 0}% · ${blocking.length} blocking, ${advisory.length} advisory`,
      createdAt: AT,
    };
  }
}

// ---- helpers ----

function reasonFor(t: PlaylistTrackInput, req: DJSEMPlaylistBuildRequest, idx: number, total: number): string {
  const bits: string[] = [];
  const pos = total <= 1 ? 0 : idx / (total - 1);
  const targetE = arcEnergyAt(req.energyArc, pos);
  bits.push(`${req.energyArc ?? "steady"} slot ${idx + 1}/${total} (target energy ${(targetE * 100) | 0}%)`);
  if (t.energy != null) bits.push(`track energy ${(t.energy * 100) | 0}%`);
  if (t.bpm) bits.push(`${t.bpm} BPM`);
  if (t.key) bits.push(`key ${t.key}`);
  if (req.requiredTrackIds.includes(t.id)) bits.push("required track");
  return bits.join(" · ");
}

function warningMessage(code: DJSEMPlaylistWarningCode, count: number): string {
  const m: Record<DJSEMPlaylistWarningCode, string> = {
    missing_platform_ref: `${count} track(s) have no platform reference for the selected platform.`,
    missing_local_file: `${count} track(s) have no local audio file.`,
    rights_unknown: `${count} track(s) have unknown rights/provenance.`,
    explicit_content: `${count} track(s) are explicit and the policy is not "allow".`,
    duplicate_track: `${count} duplicate track(s) detected.`,
    weak_transition: `Weak transition detected.`,
    duration_short: `Playlist shorter than target.`,
    duration_long: `Playlist longer than target.`,
    metadata_low_confidence: `${count} track(s) have low-confidence metadata.`,
  };
  return m[code];
}

function scorePlanItems(items: DJSEMPlaylistPlanItem[], warnings: DJSEMPlaylistPlanWarning[]): number {
  if (!items.length) return 0;
  let score = 1;
  for (const w of warnings) score -= w.severity === "error" ? 0.12 : w.severity === "warning" ? 0.05 : 0.01;
  return Math.max(0, Math.min(1, score));
}

function deriveTitle(req: DJSEMPlaylistBuildRequest): string {
  const parts: string[] = [];
  if (req.desiredDurationMinutes) parts.push(`${req.desiredDurationMinutes}min`);
  if (req.allowedGenres[0]) parts.push(req.allowedGenres[0]);
  if (req.energyArc && req.energyArc !== "steady") parts.push(req.energyArc);
  parts.push("playlist");
  return parts.join(" ");
}
